import {
  CheckOutlined,
  CloseCircleOutlined,
  DownOutlined,
} from '@ant-design/icons'
import { Button, Drawer, Input } from 'antd'
import { SelectProps as AntSelectProps, SelectValue } from 'antd/lib/select'
import React from 'react'
import styled from 'styled-components'

type OptionItem = {
  label: React.ReactNode
  value: string | number
}

type Props = Omit<AntSelectProps<SelectValue>, 'mode'> & {
  title?: React.ReactNode
}

/**
 * Parse <Select.Option> children into OptionItem[]
 */
function parseChildren(children: React.ReactNode): OptionItem[] {
  const items: OptionItem[] = []

  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return
    const { value, children: label } = child.props as {
      value: string | number
      children: React.ReactNode
    }
    if (value !== undefined) {
      items.push({ value, label })
    }
  })

  return items
}

const MobileSelect: React.FC<Props> = ({
  value,
  onChange,
  options: optionsProp,
  placeholder = '請選擇',
  disabled = false,
  allowClear = false,
  title = '請選擇',
  className,
  children,
  ...restProps
}) => {
  const [open, setOpen] = React.useState(false)

  const options: OptionItem[] = React.useMemo(() => {
    if (optionsProp && optionsProp.length > 0) {
      return optionsProp.map(o => ({
        label: o.label ?? o.value,
        value: o.value as string | number,
      }))
    }
    return parseChildren(children)
  }, [optionsProp, children])

  const selectedLabel = React.useMemo(() => {
    const found = options.find(o => o.value === value)
    return found ? found.label : undefined
  }, [options, value])

  const onOpen = () => {
    if (disabled) return
    setOpen(true)
  }

  const onSelect = (val: string | number) => {
    if (allowClear && val === value) {
      onChange?.(undefined as any, undefined as any)
    } else {
      onChange?.(val, options.find(o => o.value === val) as any)
    }
    setOpen(false)
  }

  const onClear = () => {
    onChange?.(undefined as any, undefined as any)
    setOpen(false)
  }

  return (
    <>
      <TriggerInput
        className={className}
        readOnly
        value={selectedLabel != null ? String(selectedLabel) : ''}
        placeholder={placeholder as string}
        disabled={disabled}
        onClick={onOpen}
        suffix={<DownOutlined />}
      />

      <StyledDrawer
        placement="bottom"
        closable
        title={title}
        extra={
          value != null ? (
            <ClearButton type="text" size="small" onClick={onClear}>
              <CloseCircleOutlined /> 清空
            </ClearButton>
          ) : null
        }
        open={open}
        onClose={() => setOpen(false)}
        height="auto"
        bodyStyle={{ maxHeight: '50vh', overflowY: 'auto', padding: 0 }}>
        <OptionList>
          {options.map(opt => {
            const isSelected = opt.value === value
            return (
              <OptionRow
                key={opt.value}
                role="option"
                aria-selected={isSelected}
                data-selected={isSelected}
                onClick={() => onSelect(opt.value)}>
                <span>{opt.label}</span>
                {isSelected && <CheckOutlined />}
              </OptionRow>
            )
          })}
        </OptionList>
      </StyledDrawer>
    </>
  )
}

const TriggerInput = styled(Input)`
  cursor: pointer;

  &.ant-input-affix-wrapper {
    border-radius: 4px;
    background-color: ${p => p.theme.select.bg};
    border-color: ${p => p.theme.select.borderColor};
    color: ${p => p.theme.select.color};

    &:hover,
    &:focus,
    &.ant-input-affix-wrapper-focused {
      border-color: ${p => p.theme.select.hoverAndFocusBorderColor};
      box-shadow: none;
    }

    &.ant-input-affix-wrapper-disabled {
      background: ${p => p.theme.select.disabledBg};
      color: ${p => p.theme.select.disabledColor};
      cursor: not-allowed;
    }

    input {
      background: transparent;
      color: ${p => p.theme.select.color};
      cursor: pointer;

      &::placeholder {
        color: ${p => p.theme.select.placeholderColor};
      }

      &[disabled] {
        color: ${p => p.theme.select.disabledColor};
        cursor: not-allowed;
      }
    }

    .ant-input-suffix {
      color: ${p => p.theme.select.arrowDownIconFill};
    }
  }
`

const StyledDrawer = styled(Drawer)`
  .ant-drawer-content {
    background-color: ${p => p.theme.modal.bg};
    border-radius: 12px 12px 0 0;
  }

  .ant-drawer-header {
    background-color: ${p => p.theme.modal.bg};
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    .ant-drawer-title {
      color: ${p => p.theme.modal.color};
    }

    .ant-drawer-close {
      color: ${p => p.theme.modal.color};
    }
  }

  .ant-drawer-body {
    background-color: ${p => p.theme.modal.bg};
  }
`

const ClearButton = styled(Button)`
  &.ant-btn-text {
    color: ${p => p.theme.modal.color};
    display: inline-flex;
    align-items: center;
    gap: 4px;

    &:hover {
      color: #ff4d4f;
    }
  }
`

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
`

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  color: ${p => p.theme.select.dropdown.item.color};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${p => p.theme.select.dropdown.item.hoverBg};
    color: ${p => p.theme.select.dropdown.item.hoverColor};
  }

  &[data-selected='true'] {
    font-weight: ${p => p.theme.select.dropdown.item.selectedFontWeight};
    color: ${p => p.theme.select.dropdown.item.selectedColor};
    background-color: ${p => p.theme.select.dropdown.item.selectedBg};
  }

  & + & {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
  }
`

MobileSelect.displayName = 'MobileSelect'

export default MobileSelect
